import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Radar, Rss, Youtube, Plus, Trash2, Sparkles, Loader2, PenLine,
  ExternalLink, Layers, RefreshCw, Mail,
} from "lucide-react";

const buildPrefill = (a) => ({
  sourceType: "link",
  input:
    `Angle éditorial : ${a.title}\n${a.angle}\n\n` +
    (a.pillar ? `Pilier de contenu : ${a.pillar}\n` : "") +
    (a.source_title ? `Source d'inspiration : ${a.source_title}${a.source_link ? `, ${a.source_link}` : ""}\n` : "") +
    (a.source_summary ? `Résumé de la source : ${a.source_summary}\n` : "") +
    `\nConsigne : rédige le post selon cet angle, dans ma voix. Si tu t'inspires directement de la source, cite-la (nom, et le lien si pertinent).`,
});

export default function Veille() {
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [digest, setDigest] = useState(null);
  const [emailWeekly, setEmailWeekly] = useState(false);
  const [url, setUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/watch/sources").then((r) => setSources(r.data)).catch(() => {});
    api.get("/watch/digest").then((r) => setDigest(r.data)).catch(() => {});
    api.get("/watch/settings").then((r) => setEmailWeekly(!!r.data.email_weekly)).catch(() => {});
  };

  useEffect(() => {
    load();
    api.post("/watch/seen").catch(() => {}); // marque les angles auto comme vus
  }, []);

  const addSource = async () => {
    if (!url.trim()) return;
    setAdding(true);
    try {
      await api.post("/watch/sources", { url: url.trim() });
      setUrl("");
      toast.success("Source ajoutée à votre veille.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const removeSource = async (id) => {
    await api.delete(`/watch/sources/${id}`);
    toast.success("Source retirée.");
    load();
  };

  const toggleEmail = async () => {
    const next = !emailWeekly;
    setEmailWeekly(next);
    try { await api.put("/watch/settings", { email_weekly: next }); }
    catch { setEmailWeekly(!next); }
  };

  const pollJob = (jobId) => new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = async () => {
      try {
        const { data } = await api.get(`/jobs/${jobId}`);
        if (data.status === "done") return resolve(data);
        if (data.status === "error") return reject(new Error(data.error || "Échec."));
        if (Date.now() - started > 5 * 60 * 1000) return reject(new Error("Délai dépassé."));
        setTimeout(tick, 2500);
      } catch (e) { reject(e); }
    };
    tick();
  });

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/watch/digest");
      await pollJob(data.job_id);
      const r = await api.get("/watch/digest");
      setDigest(r.data);
      toast.success(`${r.data?.angles?.length || 0} angles générés.`);
    } catch (err) {
      toast.error(err.message || formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const writePost = (a) => navigate("/creer", { state: { prefill: buildPrefill(a) } });

  const angles = digest?.angles || [];

  return (
    <div className="p-8 lg:p-12 max-w-5xl">
      <div className="mb-2 flex items-center gap-2 text-coral-dark">
        <Radar className="h-4 w-4" />
        <span className="text-xs font-semibold uppercase tracking-[0.2em]">Veille &amp; idées</span>
      </div>
      <h1 className="font-serif text-4xl text-slate-900 mb-2">Vos angles de la semaine</h1>
      <p className="text-slate-500 mb-8">
        Suivez vos sources (blogs, médias, chaînes YouTube). Chaque lundi, Pulse en tire 3-4 angles reliés à vos piliers.
      </p>

      {/* Sources */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl text-slate-900">Mes sources</h3>
          <button data-testid="toggle-email-weekly" onClick={toggleEmail}
            className={`flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
              emailWeekly ? "border-coral bg-coral-soft text-coral-dark" : "border-slate-200 text-slate-500 hover:border-coral"
            }`}>
            <Mail className="h-3.5 w-3.5" /> E-mail hebdo {emailWeekly ? "activé" : "désactivé"}
          </button>
        </div>

        <div className="flex gap-2 mb-4">
          <input data-testid="watch-url-input" value={url} onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addSource()}
            placeholder="URL d'un blog/média ou d'une chaîne YouTube (youtube.com/@nom)…"
            className="flex-1 border border-slate-200 rounded-sm px-3.5 py-2.5 text-sm outline-none focus:ring-1 focus:ring-coral focus:border-coral transition-colors" />
          <button data-testid="add-source-btn" onClick={addSource} disabled={adding}
            className="bg-coral-strong text-white px-4 rounded-sm text-sm font-semibold hover:bg-coral-hover transition-colors disabled:opacity-60 flex items-center gap-2">
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Ajouter
          </button>
        </div>

        {sources.length === 0 ? (
          <p className="text-sm text-slate-400 py-4 text-center">Aucune source pour l'instant. Ajoutez vos blogs, médias et chaînes YouTube préférés.</p>
        ) : (
          <div className="space-y-2" data-testid="sources-list">
            {sources.map((s) => (
              <div key={s.id} data-testid={`source-${s.id}`} className="flex items-center justify-between gap-3 border border-slate-100 rounded-sm px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  {s.type === "youtube"
                    ? <Youtube className="h-4 w-4 text-red-500 shrink-0" />
                    : <Rss className="h-4 w-4 text-coral shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{s.title}</p>
                    <p className="text-xs text-slate-400 truncate">{s.recent_items?.length || 0} contenus récents · {s.url}</p>
                  </div>
                </div>
                <button data-testid={`delete-source-${s.id}`} onClick={() => removeSource(s.id)}
                  className="text-slate-400 hover:text-destructive transition-colors shrink-0">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Generate */}
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-serif text-2xl text-slate-900">Angles éditoriaux</h3>
        <button data-testid="generate-angles-btn" onClick={generate} disabled={busy || sources.length === 0}
          className="flex items-center gap-2 bg-coral-strong text-white px-5 py-2.5 rounded-sm text-sm font-semibold hover:bg-coral-hover transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {busy ? "Génération…" : "Générer mes angles"}
        </button>
      </div>

      {angles.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-sm p-10 text-center">
          <Sparkles className="h-6 w-6 text-coral mx-auto mb-3" />
          <p className="text-sm text-slate-500">
            {sources.length === 0
              ? "Ajoutez au moins une source, puis générez vos angles."
              : "Cliquez sur « Générer mes angles » pour obtenir vos idées de la semaine."}
          </p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="angles-list">
          {angles.map((a, i) => (
            <div key={i} data-testid={`angle-${i}`} className="fade-in-up bg-white border border-slate-200 rounded-sm p-6" style={{ animationDelay: `${i * 0.08}s` }}>
              <div className="flex items-start justify-between gap-4 mb-2">
                <h4 className="font-serif text-xl text-slate-900 leading-snug">{a.title}</h4>
                {a.pillar && (
                  <span className="shrink-0 flex items-center gap-1.5 text-xs font-semibold text-coral-dark bg-coral-soft px-2.5 py-1 rounded-full">
                    <Layers className="h-3 w-3" /> {a.pillar}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">{a.angle}</p>
              <div className="flex items-center justify-between gap-3 pt-3 border-t border-slate-100">
                {a.source_link ? (
                  <a href={a.source_link} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-coral-dark transition-colors truncate">
                    <ExternalLink className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{a.source_title || a.source_link}</span>
                  </a>
                ) : <span className="text-xs text-slate-400 truncate">{a.source_title}</span>}
                <button data-testid={`write-post-${i}`} onClick={() => writePost(a)}
                  className="shrink-0 flex items-center gap-2 text-sm font-semibold text-white bg-coral-strong px-4 py-2 rounded-sm hover:bg-coral-hover transition-colors">
                  <PenLine className="h-4 w-4" /> Rédiger ce post
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
